/**
 * CreditsAPI - Service for credit management
 */

import { APIService, APIServiceError } from './APIService';
import type {
  CreditBalanceResponse,
  UseCreditResponse,
  PurchaseCreditsResponse,
  CreditOperation,
} from '../../types/credits';

/**
 * API endpoint for credits
 */
const CREDITS_API_URL =
  import.meta.env.VITE_API_CREDITS_URL ||
  'https://xyeqvofyij.execute-api.us-west-2.amazonaws.com/prod';

/**
 * CreditsAPI service for credit management
 */
export class CreditsAPI extends APIService {
  /**
   * Get current user's credit balance
   * 
   * @returns Credit balance
   * @throws APIServiceError if not authenticated or request fails
   */
  async getCredits(): Promise<number> {
    if (!this.isAuthenticated()) {
      throw new APIServiceError('Not authenticated', 401);
    }

    try {
      const response = await this.fetchWithRetry(`${CREDITS_API_URL}/credits`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...this.getAuthHeader(),
        },
        retries: 2,
        timeout: 10000,
      });

      const result: CreditBalanceResponse = await response.json();

      if (typeof result.credits !== 'number') {
        throw new APIServiceError('Invalid response format', 500);
      }

      return result.credits;
    } catch (error) {
      if (error instanceof APIServiceError) {
        throw error;
      }

      throw new APIServiceError(
        `Failed to fetch credits: ${(error as Error).message}`,
        500
      );
    }
  }

  /**
   * Use a credit for an operation
   * 
   * @param operation - Type of operation (generate, export, share)
   * @returns Remaining credits
   * @throws APIServiceError if not authenticated or request fails
   */
  async useCredit(operation: CreditOperation = 'generate'): Promise<number> {
    if (!this.isAuthenticated()) {
      throw new APIServiceError('Not authenticated', 401);
    }

    try {
      const response = await this.fetchWithRetry(`${CREDITS_API_URL}/credits/use`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...this.getAuthHeader(),
        },
        body: JSON.stringify({ operation }),
        retries: 1, // Don't retry credit deduction
        timeout: 10000,
      });

      const result: UseCreditResponse = await response.json();

      if (typeof result.remainingCredits !== 'number') {
        throw new APIServiceError('Invalid response format', 500);
      }

      return result.remainingCredits;
    } catch (error) {
      if (error instanceof APIServiceError) {
        throw error;
      }

      throw new APIServiceError(
        `Failed to use credit: ${(error as Error).message}`,
        500
      );
    }
  }

  /**
   * Purchase credits
   * 
   * @param packageId - Credit package ID
   * @returns Purchase response with checkout URL
   * @throws APIServiceError if not authenticated or request fails
   */
  async purchaseCredits(packageId: string): Promise<PurchaseCreditsResponse> {
    if (!this.isAuthenticated()) {
      throw new APIServiceError('Not authenticated', 401);
    }

    if (!packageId) {
      throw new APIServiceError('Package ID is required', 400);
    }

    try {
      const response = await this.fetchWithRetry(
        `${CREDITS_API_URL}/credits/purchase`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...this.getAuthHeader(),
          },
          body: JSON.stringify({ packageId }),
          retries: 1,
          timeout: 15000,
        }
      );

      const result: PurchaseCreditsResponse = await response.json();

      if (!result.checkoutUrl) {
        throw new APIServiceError('Invalid response - missing checkout URL', 500);
      }

      return result;
    } catch (error) {
      if (error instanceof APIServiceError) {
        throw error;
      }

      throw new APIServiceError(
        `Failed to initiate purchase: ${(error as Error).message}`,
        500
      );
    }
  }

  /**
   * Check if user has sufficient credits
   * 
   * @param required - Number of credits required
   * @returns True if user has sufficient credits
   */
  async hasCredits(required: number = 1): Promise<boolean> {
    try {
      const balance = await this.getCredits();
      return balance >= required;
    } catch {
      return false;
    }
  }
}

// Export singleton instance
export const creditsAPI = new CreditsAPI();
